import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { MAP_TILE_ATTRIBUTION, MAP_TILE_URL } from 'src/app/global/consts/map-tiles.const';
import { AdminPermitDetail, permitFileName, PopulatedUser } from '../../models/permit.model';
import { I18nString } from '../../models/permit-category.model';

interface PrintSection {
    key: string;
    label: string;
    group: 'citizen' | 'internal';
    selected: boolean;
    disabled: boolean;
}

export interface PermitPrintDialogData {
    permit: AdminPermitDetail;
}

@Component({
    selector: 'app-permit-print-dialog',
    templateUrl: './permit-print-dialog.component.html',
    styleUrls: ['./permit-print-dialog.component.scss'],
    providers: [DatePipe],
})
export class PermitPrintDialogComponent {
    sections: PrintSection[];

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: PermitPrintDialogData,
        private readonly dialogRef: MatDialogRef<PermitPrintDialogComponent>,
        private translate: TranslateService,
        private datePipe: DatePipe,
    ) {
        const permit = data.permit;
        const hasApplicant = !!(permit.applicant?.firstName || permit.applicant?.lastName || permit.applicant?.email);
        const hasFormData = Object.keys(permit.formData || {}).length > 0;
        const hasAddress = !!(permit.location?.address || permit.location?.siteLocation);
        const hasMap = !!(permit.location?.coordinates?.lat && permit.location?.coordinates?.lng);
        const hasFees = !!permit.fees?.length;

        this.sections = [
            {
                key: 'info',
                label: this.t('permit.manage.print.section.info'),
                group: 'citizen',
                selected: true,
                disabled: false,
            },
            {
                key: 'applicant',
                label: this.t('permit.manage.print.section.applicant'),
                group: 'citizen',
                selected: hasApplicant,
                disabled: !hasApplicant,
            },
            {
                key: 'form_data',
                label: this.t('permit.manage.print.section.form_data'),
                group: 'citizen',
                selected: hasFormData,
                disabled: !hasFormData,
            },
            {
                key: 'address',
                label: this.t('permit.manage.print.section.address'),
                group: 'citizen',
                selected: hasAddress,
                disabled: !hasAddress,
            },
            {
                key: 'map',
                label: this.t('permit.manage.print.section.map'),
                group: 'citizen',
                selected: hasMap,
                disabled: !hasMap,
            },
            {
                key: 'fees',
                label: this.t('permit.manage.print.section.fees'),
                group: 'citizen',
                selected: hasFees,
                disabled: !hasFees,
            },
            {
                key: 'messages',
                label: this.t('permit.manage.print.section.messages'),
                group: 'citizen',
                selected: !!permit.messages?.length,
                disabled: !permit.messages?.length,
            },
            {
                key: 'tasks',
                label: this.t('permit.manage.print.section.tasks'),
                group: 'internal',
                selected: !!permit.subtasks?.length,
                disabled: !permit.subtasks?.length,
            },
            {
                key: 'linked',
                label: this.t('permit.manage.print.section.linked'),
                group: 'internal',
                selected: !!permit.links?.length,
                disabled: !permit.links?.length,
            },
            {
                key: 'tags',
                label: this.t('permit.manage.print.section.tags'),
                group: 'internal',
                selected: !!permit.tags?.length,
                disabled: !permit.tags?.length,
            },
            {
                key: 'internal_files',
                label: this.t('permit.manage.print.section.internal_files'),
                group: 'internal',
                selected: !!permit.internalFiles?.length,
                disabled: !permit.internalFiles?.length,
            },
            {
                key: 'notes',
                label: this.t('permit.manage.print.section.notes'),
                group: 'internal',
                selected: !!permit.internalNotes?.length,
                disabled: !permit.internalNotes?.length,
            },
            {
                key: 'history',
                label: this.t('permit.manage.print.section.history'),
                group: 'internal',
                selected: !!permit.history?.length,
                disabled: !permit.history?.length,
            },
        ];
    }

    get citizenSections(): PrintSection[] {
        return this.sections.filter((s) => s.group === 'citizen');
    }

    get internalSections(): PrintSection[] {
        return this.sections.filter((s) => s.group === 'internal');
    }

    get selectedCount(): number {
        return this.sections.filter((s) => s.selected).length;
    }

    toggleByKey(key: string): void {
        const section = this.sections.find((s) => s.key === key);
        if (section && !section.disabled) {
            section.selected = !section.selected;
        }
    }

    print(): void {
        const selected = new Set(this.sections.filter((s) => s.selected).map((s) => s.key));
        this.openPrintWindow(selected);
        this.dialogRef.close();
    }

    cancel(): void {
        this.dialogRef.close();
    }

    private t(key: string): string {
        return this.translate.instant(key);
    }

    private i18nName(name: I18nString | string): string {
        if (!name) return '';
        if (typeof name === 'string') return name;
        const lang = this.translate.currentLang as keyof I18nString;
        return name[lang] || name.fr || name.en || '';
    }

    private userName(user: string | PopulatedUser): string {
        if (!user || typeof user === 'string') return '';
        return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }

    private formatDate(date: Date | string): string {
        if (!date) return '';
        const locale = this.translate.currentLang === 'en' ? 'en-CA' : 'fr-CA';
        return this.datePipe.transform(date, 'd MMM yyyy', undefined, locale) || '';
    }

    private formatDateTime(date: Date | string): string {
        if (!date) return '';
        const locale = this.translate.currentLang === 'en' ? 'en-CA' : 'fr-CA';
        return this.datePipe.transform(date, 'd MMM yyyy HH:mm', undefined, locale) || '';
    }

    private formatCurrency(amount: number): string {
        if (amount === null || amount === undefined) return '';
        return new Intl.NumberFormat(this.translate.currentLang === 'en' ? 'en-CA' : 'fr-CA', {
            style: 'currency',
            currency: 'CAD',
            currencyDisplay: 'narrowSymbol',
        } as any).format(Number(amount));
    }

    private formatFormValue(value: any): string {
        if (value === null || value === undefined) return '';
        if (Array.isArray(value)) return value.join(', ');
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
    }

    private esc(str: string): string {
        if (!str) return '';
        return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    private nl2br(str: string): string {
        return this.esc(str).replace(/\n/g, '<br>');
    }

    private getInitials(firstName: string, lastName: string): string {
        const f = (firstName || '').charAt(0).toUpperCase();
        const l = (lastName || '').charAt(0).toUpperCase();
        return f + l;
    }

    private openPrintWindow(selected: Set<string>): void {
        const permit = this.data.permit;
        const esc = (s: string) => this.esc(s);
        const nl2br = (s: string) => this.nl2br(s);
        const fd = (d: Date | string) => this.formatDate(d);
        const fdt = (d: Date | string) => this.formatDateTime(d);

        // Injected into inline styles and the map-pin SVG (with a 2-digit alpha
        // suffix appended), so only a #RRGGBB literal is allowed through.
        const statusColor = /^#[0-9a-fA-F]{6}$/.test(permit.state?.color || '') ? permit.state.color : '#6b7280';
        const statusName = esc(this.i18nName(permit.state?.name));
        const categoryName = esc(this.i18nName(permit.category?.name));
        const typeName = esc(this.i18nName(permit.permitType?.name));
        const deptName = esc(this.i18nName(permit.department?.name));
        const printTitle = `${this.t('permit.manage.print.title')} ${permit.permitId}`;

        let body = '';
        const includesMap = selected.has('map') && !!(permit.location?.coordinates?.lat && permit.location?.coordinates?.lng);

        const sectionHeader = (label: string) => `<div class="section-header"><h2 class="section-title">${label}</h2></div>`;

        // ---- Info section (card: breadcrumb + status + dates) ----
        if (selected.has('info')) {
            const typeHtml = typeName ? `<div class="crumb-row">└ ${typeName}</div>` : '';
            const deptHtml = deptName ? `<div class="crumb-dept">└ ${deptName}</div>` : '';
            const createdAtLabel = esc(this.t('permit.manage.print.field.created_at'));
            const validityStartLabel = esc(this.t('permit.manage.print.field.validity_start'));
            const validityEndLabel = esc(this.t('permit.manage.print.field.validity_end'));
            const externalNumberLabel = esc(this.t('permit.manage.print.field.external_number'));
            const statusHtml = statusName
                ? `<div class="info-status-pill" style="background:${statusColor}22;border:1px solid ${statusColor}66;color:${statusColor};"><span class="info-status-dot" style="background:${statusColor};"></span>${statusName}</div>`
                : '';
            body += `
                <section class="print-section">
                    <div class="info-card">
                        <div class="info-card-left">
                            <div class="crumb-main">${categoryName}</div>
                            ${typeHtml}
                            ${deptHtml}
                        </div>
                        <div class="info-card-right">
                            ${statusHtml}
                            ${permit.createdAt ? `<div class="info-date"><span class="info-date-label">${createdAtLabel}: </span>${esc(fd(permit.createdAt))}</div>` : ''}
                            ${permit.validityStartDate ? `<div class="info-date"><span class="info-date-label">${validityStartLabel}: </span>${esc(fd(permit.validityStartDate))}</div>` : ''}
                            ${permit.validityEndDate ? `<div class="info-date"><span class="info-date-label">${validityEndLabel}: </span>${esc(fd(permit.validityEndDate))}</div>` : ''}
                            ${permit.externalPermitNumber ? `<div class="info-date"><span class="info-date-label">${externalNumberLabel}: </span>${esc(permit.externalPermitNumber)}</div>` : ''}
                        </div>
                    </div>
                </section>`;
        }

        // ---- Applicant + Address side by side ----
        let applicantInner = '';
        if (selected.has('applicant')) {
            const firstName = permit.applicant?.firstName || '';
            const lastName = permit.applicant?.lastName || '';
            const fullName = `${firstName} ${lastName}`.trim();
            const email = permit.applicant?.email || '';
            const phone = permit.applicant?.phone || '';
            const initials = this.getInitials(firstName, lastName);
            applicantInner = `
                <div class="col-label">${esc(this.t('permit.manage.print.section.applicant'))}</div>
                <div class="requester-row">
                    <div class="user-avatar">${esc(initials)}</div>
                    <span class="user-name">${esc(fullName)}</span>
                </div>
                <div class="contact-rows">
                    ${phone ? `<div class="contact-row"><span class="material-icons contact-icon">phone</span><span class="contact-text">${esc(phone)}</span></div>` : ''}
                    ${email ? `<div class="contact-row"><span class="material-icons contact-icon">email</span><span class="contact-text">${esc(email)}</span></div>` : ''}
                    ${permit.applicant?.address ? `<div class="contact-row"><span class="material-icons contact-icon">home</span><span class="contact-text">${esc(permit.applicant.address)}</span></div>` : ''}
                </div>`;
        }

        let addressInner = '';
        if (selected.has('address')) {
            const line1 = permit.location?.address || '';
            const line2 = permit.location?.siteLocation || '';
            if (line1 || line2) {
                addressInner = `
                    <div class="col-label">${esc(this.t('permit.manage.print.section.address'))}</div>
                    <div class="address-icon-row">
                        <span class="material-icons address-icon">place</span>
                        <div class="address-lines">
                            ${line1 ? `<span class="address-line">${esc(line1)}</span>` : ''}
                            ${line2 ? `<span class="address-line address-line-city">${esc(line2)}</span>` : ''}
                        </div>
                    </div>`;
            }
        }

        if (applicantInner || addressInner) {
            body += `
                <section class="print-section">
                    <div class="citizen-row">
                        ${applicantInner ? `<div class="citizen-col">${applicantInner}</div>` : ''}
                        ${addressInner ? `<div class="citizen-col">${addressInner}</div>` : ''}
                    </div>
                </section>`;
        }

        // ---- Map section (placeholder div — Leaflet inits in <script>) ----
        if (includesMap) {
            body += `
                <section class="print-section">
                    ${sectionHeader(esc(this.t('permit.manage.print.section.map')))}
                    <div id="print-map"></div>
                </section>`;
        }

        // ---- Form data section ----
        if (selected.has('form_data')) {
            const entries = Object.entries(permit.formData || {});
            if (entries.length) {
                body += `
                    <section class="print-section">
                        ${sectionHeader(esc(this.t('permit.manage.print.section.form_data')))}
                        <div class="form-data-list">
                        ${entries
                            .map(
                                ([key, value]) => `<div class="form-data-item">
                                    <span class="form-data-label">${esc(key)}</span>
                                    <span class="form-data-value">${nl2br(this.formatFormValue(value))}</span>
                                </div>`,
                            )
                            .join('')}
                        </div>
                    </section>`;
            }
        }

        // ---- Fees section ----
        if (selected.has('fees') && permit.fees?.length) {
            const paidLabel = this.t('permit.manage.info.fee_paid');
            const unpaidLabel = this.t('permit.manage.info.fee_unpaid');
            body += `
                <section class="print-section">
                    ${sectionHeader(esc(this.t('permit.manage.print.section.fees')))}
                    <ul class="fees-list">
                        ${permit.fees
                            .map((fee) => {
                                const timing = this.t(fee.timing === 'upfront' ? 'permit.manage.info.fee_timing_upfront' : 'permit.manage.info.fee_timing_later');
                                return `<li class="fee-item">
                                    <span class="fee-desc">${esc(fee.description || '')}</span>
                                    <span class="fee-timing">${esc(timing)}</span>
                                    <span class="fee-paid ${fee.paid ? 'fee-is-paid' : ''}">${esc(fee.paid ? paidLabel : unpaidLabel)}${fee.paid && fee.paidAt ? ` · ${esc(fd(fee.paidAt))}` : ''}</span>
                                    <span class="fee-amount">${esc(this.formatCurrency(fee.amount))}</span>
                                </li>`;
                            })
                            .join('')}
                    </ul>
                </section>`;
        }

        // ---- Messages section ----
        if (selected.has('messages') && permit.messages?.length) {
            body += `
                <section class="print-section">
                    ${sectionHeader(esc(this.t('permit.manage.print.section.messages')))}
                    <div class="messages-list">
                    ${permit.messages
                        .map((m) => {
                            const isAdmin = m.senderType === 'agent';
                            const sender = this.userName(m.sender) || (isAdmin ? this.t('permit.manage.print.admin') : this.t('permit.manage.print.citizen'));
                            const badge = isAdmin ? this.t('permit.manage.print.admin') : this.t('permit.manage.print.citizen');
                            return `<div class="message-item ${isAdmin ? 'message-admin' : 'message-citizen'}">
                                <div class="message-meta">
                                    <span class="message-sender">${esc(sender)}</span>
                                    <span class="message-badge">${esc(badge)}</span>
                                    <span class="message-date">${esc(fdt(m.createdAt))}</span>
                                </div>
                                <p class="message-text">${nl2br(m.text)}</p>
                            </div>`;
                        })
                        .join('')}
                    </div>
                </section>`;
        }

        // ---- Tasks section ----
        if (selected.has('tasks') && permit.subtasks?.length) {
            body += `
                <section class="print-section">
                    ${sectionHeader(esc(this.t('permit.manage.print.section.tasks')))}
                    <ul class="tasks-list">
                        ${permit.subtasks
                            .map((task) => {
                                const done = task.isDone || task.completed;
                                const assigneeName = this.userName(task.assignedTo);
                                const assignee = assigneeName ? `${esc(assigneeName.split(' ')[0])} ${esc((assigneeName.split(' ')[1] || '').charAt(0).toUpperCase())}.` : '';
                                return `<li class="task-item${done ? ' task-done' : ''}">
                                    <span class="task-check">${done ? '&#9745;' : '&#9744;'}</span>
                                    <span class="task-desc">${esc(task.title)}</span>
                                    ${assignee ? `<span class="task-assignee">${assignee}</span>` : ''}
                                    ${task.dueDate ? `<span class="task-due"><span class="task-date-label">${esc(this.t('permit.manage.subtasks.due_date'))}</span> ${esc(fd(task.dueDate))}</span>` : ''}
                                    ${task.completedAt ? `<span class="task-completed"><span class="task-date-label">${esc(this.t('permit.manage.print.field.completed_at'))}</span> ${esc(fd(task.completedAt))}</span>` : ''}
                                </li>`;
                            })
                            .join('')}
                    </ul>
                </section>`;
        }

        // ---- Linked permits section ----
        if (selected.has('linked') && permit.links?.length) {
            const linkLabels: Record<string, string> = {
                BLOCKS: this.t('permit.manage.links.type_BLOCKS'),
                IS_BLOCKED_BY: this.t('permit.manage.links.type_IS_BLOCKED_BY'),
                IS_DUPLICATE_OF: this.t('permit.manage.links.type_IS_DUPLICATE_OF'),
                IS_LINKED_TO: this.t('permit.manage.links.type_IS_LINKED_TO'),
            };
            body += `
                <section class="print-section">
                    ${sectionHeader(esc(this.t('permit.manage.print.section.linked')))}
                    <ul class="links-list">
                        ${permit.links
                            .map((link) => {
                                const type = link.linkType || link.type || '';
                                const typeLabel = linkLabels[type] || type;
                                return `<li class="link-item">
                                    <span class="link-type">${esc(typeLabel)}</span>
                                    <span class="link-id">${esc(link.linkedPermitId || '')}</span>
                                </li>`;
                            })
                            .join('')}
                    </ul>
                </section>`;
        }

        // ---- Tags section ----
        if (selected.has('tags') && permit.tags?.length) {
            body += `
                <section class="print-section">
                    ${sectionHeader(esc(this.t('permit.manage.print.section.tags')))}
                    <div class="tags-list">
                        ${permit.tags.map((tag) => `<span class="tag">${esc(tag)}</span>`).join('')}
                    </div>
                </section>`;
        }

        // ---- Internal files section ----
        if (selected.has('internal_files') && permit.internalFiles?.length) {
            body += `
                <section class="print-section">
                    ${sectionHeader(esc(this.t('permit.manage.print.section.internal_files')))}
                    <ul class="files-list">
                        ${permit.internalFiles.map((f) => `<li>${esc(permitFileName(f))}</li>`).join('')}
                    </ul>
                </section>`;
        }

        // ---- Notes section ----
        if (selected.has('notes') && permit.internalNotes?.length) {
            body += `
                <section class="print-section">
                    ${sectionHeader(esc(this.t('permit.manage.print.section.notes')))}
                    <div class="notes-list">
                    ${permit.internalNotes
                        .map((note) => {
                            const author = this.userName(note.author);
                            return `<div class="note-item">
                                <div class="note-meta">
                                    <span class="note-author">${esc(author)}</span>
                                    <span class="note-date">${esc(fdt(note.createdAt))}</span>
                                    ${note.edited ? `<span class="note-edited">(${esc(this.t('permit.manage.print.edited'))})</span>` : ''}
                                </div>
                                <p class="note-text">${nl2br(note.text)}</p>
                            </div>`;
                        })
                        .join('')}
                    </div>
                </section>`;
        }

        // ---- History section ----
        if (selected.has('history') && permit.history?.length) {
            body += `
                <section class="print-section">
                    ${sectionHeader(esc(this.t('permit.manage.print.section.history')))}
                    <div class="history-list">
                    ${permit.history
                        .map((h) => {
                            const actor = this.userName(h.actor);
                            const actionKey = `permit.manage.history.action_${h.action}`;
                            const actionLabel = this.t(actionKey);
                            const action = actionLabel === actionKey ? h.action : actionLabel;
                            return `<div class="history-item">
                                <span class="history-date">${esc(fdt(h.timestamp))}</span>
                                <span class="history-user">${esc(actor)}</span>
                                <span class="history-action">${esc(h.description || action || '')}</span>
                            </div>`;
                        })
                        .join('')}
                    </div>
                </section>`;
        }

        const generatedOn = `${this.t('permit.manage.print.generated_on')} ${fdt(new Date().toISOString())}`;

        const mapLat = includesMap ? permit.location.coordinates.lat : 0;
        const mapLng = includesMap ? permit.location.coordinates.lng : 0;

        const mapScript = includesMap
            ? `
            var map = L.map('print-map', { zoomControl: false, attributionControl: false });
            map.setView([${mapLat}, ${mapLng}], 15);
            L.tileLayer('${MAP_TILE_URL}', { maxZoom: 19, attribution: '${MAP_TILE_ATTRIBUTION}' }).addTo(map);
            var pinSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="32" viewBox="0 0 26 33"><path d="M13 1C8.03 1 4 5.01 4 10.13c0 4.62 4.02 10.55 9 13.31C18 20.68 22 14.75 22 10.13 22 5.01 17.97 1 13 1z" fill="${statusColor}" stroke="white" stroke-width="1.5"/></svg>';
            var icon = L.divIcon({ html: pinSvg, className: '', iconSize: [24,32], iconAnchor: [12,32] });
            L.marker([${mapLat}, ${mapLng}], { icon: icon }).addTo(map);
            setTimeout(function() { map.invalidateSize(); doPrint(); }, 1200);
        `
            : 'doPrint();';

        const leafletHead = `
            <link rel="stylesheet" href="https://fonts.googleapis.com/icon?family=Material+Icons">
            ${
                includesMap
                    ? `<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css">
            <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"><\/script>`
                    : ''
            }
        `;

        const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${esc(printTitle)}</title>
${leafletHead}
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 12px; color: #111827; background: #fff; }

  .page-header { background: #f9fafb; border-bottom: 1px solid #e5e7eb; padding: 12px 32px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
  .page-request-id { font-size: 12px; font-weight: 700; color: #374151; }
  .page-meta { font-size: 10px; color: #9ca3af; }

  .page-content { padding: 28px 32px; }

  .print-section { margin-bottom: 26px; page-break-inside: avoid; }
  .section-header { display: flex; align-items: center; margin-bottom: 10px; }
  h2.section-title { font-size: 10px; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: 0.1em; }

  .requester-row { display: flex; align-items: center; gap: 8px; }
  .user-avatar { width: 26px; height: 26px; border-radius: 50%; background-color: #e5e7eb; color: #374151; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 600; flex-shrink: 0; overflow: hidden; }
  .user-name { font-size: 14px; font-weight: 600; color: #111827; text-decoration: none; }
  .contact-rows { display: flex; flex-direction: column; gap: 5px; padding-left: 34px; margin-top: 8px; }
  .contact-row { display: flex; align-items: center; gap: 7px; }
  .contact-icon { font-size: 14px; width: 14px; height: 14px; color: #9ca3af; flex-shrink: 0; vertical-align: middle; }
  .contact-text { font-size: 13px; color: #6b7280; }

  .address-icon-row { display: flex; align-items: flex-start; gap: 6px; }
  .address-icon { font-size: 14px; width: 14px; height: 14px; color: #9ca3af; flex-shrink: 0; margin-top: 2px; vertical-align: middle; }
  .address-lines { display: flex; flex-direction: column; gap: 1px; }
  .address-line { font-size: 13px; color: #374151; line-height: 1.4; display: block; }
  .address-line-city { color: #6b7280; }

  #print-map { width: 100%; height: 220px; border-radius: 10px; border: 1px solid #e5e7eb; overflow: hidden; }

  .form-data-list { display: flex; flex-direction: column; gap: 4px; }
  .form-data-item { display: flex; gap: 12px; align-items: baseline; padding: 7px 10px; border-radius: 6px; background: #f9fafb; border: 1px solid #f3f4f6; font-size: 12px; }
  .form-data-label { font-weight: 600; color: #6b7280; width: 180px; flex-shrink: 0; font-size: 11px; overflow-wrap: anywhere; }
  .form-data-value { color: #111827; flex: 1; overflow-wrap: anywhere; }

  .fees-list { list-style: none; padding: 0; display: flex; flex-direction: column; gap: 4px; }
  .fee-item { display: flex; align-items: center; gap: 10px; padding: 7px 10px; border-radius: 6px; background: #f9fafb; border: 1px solid #f3f4f6; font-size: 12px; }
  .fee-desc { flex: 1; color: #111827; }
  .fee-timing { font-size: 10px; color: #9ca3af; background: #f3f4f6; border-radius: 4px; padding: 1px 6px; white-space: nowrap; flex-shrink: 0; }
  .fee-paid { font-size: 10px; color: #b45309; background: #fff8e1; border-radius: 4px; padding: 1px 6px; white-space: nowrap; flex-shrink: 0; }
  .fee-paid.fee-is-paid { color: #059669; background: #ecfdf5; }
  .fee-amount { font-weight: 700; color: #111827; flex-shrink: 0; }

  .messages-list { display: flex; flex-direction: column; gap: 8px; }
  .message-item { padding: 10px 12px; border-radius: 8px; border-left: 3px solid #e5e7eb; }
  .message-admin { border-left-color: #6366f1; background: #f5f3ff; }
  .message-citizen { border-left-color: #10b981; background: #f0fdf4; }
  .message-meta { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; flex-wrap: wrap; }
  .message-sender { font-weight: 700; color: #111827; font-size: 12px; }
  .message-badge { padding: 1px 7px; border-radius: 4px; background: rgba(0,0,0,0.06); color: #6b7280; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; }
  .message-date { color: #9ca3af; font-size: 10px; margin-left: auto; }
  .message-text { font-size: 12px; color: #374151; line-height: 1.6; }

  .tasks-list { list-style: none; padding: 0; display: flex; flex-direction: column; gap: 4px; }
  .task-item { display: flex; align-items: center; gap: 10px; padding: 7px 10px; border-radius: 6px; background: #f9fafb; border: 1px solid #f3f4f6; font-size: 12px; }
  .task-check { font-size: 14px; flex-shrink: 0; color: #374151; }
  .task-desc { flex: 1; color: #111827; }
  .task-done .task-desc { text-decoration: line-through; color: #9ca3af; }
  .task-assignee { font-size: 11px; font-weight: 600; color: #374151; flex-shrink: 0; }
  .task-due { font-size: 10px; color: #9ca3af; background: #f3f4f6; border-radius: 4px; padding: 1px 6px; white-space: nowrap; flex-shrink: 0; }
  .task-completed { font-size: 10px; color: #059669; background: #ecfdf5; border-radius: 4px; padding: 1px 6px; white-space: nowrap; flex-shrink: 0; }
  .task-date-label { font-weight: 600; opacity: 0.75; }

  .links-list { list-style: none; padding: 0; display: flex; flex-direction: column; gap: 4px; }
  .link-item { display: flex; align-items: center; gap: 10px; padding: 7px 10px; border-radius: 6px; background: #f9fafb; border: 1px solid #f3f4f6; font-size: 12px; }
  .link-type { font-weight: 600; color: #6b7280; width: 90px; flex-shrink: 0; font-size: 11px; }
  .link-id { font-weight: 700; font-size: 11px; background: #e5e7eb; border-radius: 5px; padding: 2px 8px; color: #374151; flex-shrink: 0; }

  .tags-list { display: flex; flex-wrap: wrap; gap: 6px; }
  .tag { padding: 4px 12px; background: #f3f4f6; border-radius: 20px; font-size: 11px; font-weight: 500; color: #374151; border: 1px solid #e5e7eb; }

  .files-list { list-style: none; padding: 0; display: flex; flex-direction: column; gap: 4px; }
  .files-list li { padding: 7px 10px; font-size: 12px; color: #374151; background: #f9fafb; border-radius: 6px; border: 1px solid #f3f4f6; display: flex; align-items: center; gap: 8px; }
  .files-list li::before { content: "📎"; font-size: 13px; }

  .notes-list { display: flex; flex-direction: column; gap: 8px; }
  .note-item { padding: 10px 12px; background: #f9fafb; border-radius: 8px; border: 1px solid #f3f4f6; border-left: 3px solid #d1d5db; }
  .note-meta { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
  .note-author { font-weight: 700; color: #111827; font-size: 12px; }
  .note-date { color: #9ca3af; font-size: 10px; }
  .note-edited { font-style: italic; color: #9ca3af; font-size: 10px; }
  .note-text { font-size: 12px; color: #374151; line-height: 1.6; }

  .history-list { display: flex; flex-direction: column; }
  .history-item { display: flex; gap: 12px; align-items: baseline; padding: 6px 0; font-size: 11px; border-bottom: 1px solid #f3f4f6; }
  .history-item:last-child { border-bottom: none; }
  .history-date { color: #9ca3af; width: 115px; flex-shrink: 0; }
  .history-user { font-weight: 600; color: #374151; width: 135px; flex-shrink: 0; }
  .history-action { color: #6b7280; flex: 1; }

  .info-card { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
  .info-card-left { flex: 1; }
  .crumb-main { font-size: 15px; font-weight: 700; color: #111827; }
  .crumb-row { font-size: 12px; color: #6b7280; margin-top: 3px; }
  .crumb-dept { font-size: 11px; color: #9ca3af; margin-top: 2px; }
  .info-card-right { display: flex; flex-direction: column; align-items: flex-end; gap: 5px; flex-shrink: 0; }
  .info-status-pill { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; }
  .info-status-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
  .info-date { font-size: 10px; color: #6b7280; }
  .info-date-label { color: #9ca3af; }

  .citizen-row { display: flex; gap: 28px; align-items: flex-start; }
  .citizen-col { flex: 1; min-width: 0; }
  .col-label { font-size: 10px; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 8px; }

  @media print {
    .page-header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    #print-map { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .print-section { page-break-inside: avoid; break-inside: avoid; }
  }
</style>
</head>
<body>
  <div class="page-header">
    <div class="page-request-id">${esc(this.t('permit.manage.print.field.permit_id'))} ${esc(permit.permitId)}</div>
    <div class="page-meta">${esc(generatedOn)}</div>
  </div>

  <div class="page-content">
    ${body}
  </div>

  <script>
    function doPrint() {
      window.print();
      window.onafterprint = function() { window.close(); };
    }
    window.onload = function() {
      ${mapScript}
    };
  <\/script>
</body>
</html>`;

        const win = window.open('', '_blank', 'width=860,height=700');
        if (!win) return;
        win.document.write(html);
        win.document.close();
    }
}
